/**
 * @fileoverview Framework-agnostic renderer that turns a DocumentLayout (or DocumentRenderData)
 * into HTML — the single rendering source the canvas preview and the PDF pipeline share
 * (G-DRY-RENDER).
 * @module document-render/renderDocumentLayout
 *
 * Responsibilities: - Emit deterministic HTML + scoped CSS for a DocumentLayout: page chrome
 *                     (size/orientation/margins/border/watermark), header/footer, and body bands.
 *                   - Emit deterministic HTML from DocumentRenderData — the data-driven path
 *                     that expands resolved sections into final HTML (A2).
 * Dependencies:     - ./documentLayout (model), ./documentConfig (Resolved* types),
 *                     ./renderSections (per-section HTML renderers).
 * Architecture:     - Pure functions; safe to run in the Node worker and the React canvas.
 *                     Two entry points:
 *                       - renderDocumentLayoutToHtml (layout-only, binding markers for dynamic bands)
 *                       - renderDocumentToHtml (data-driven, fully expanded sections)
 * Notes:            - renderDocumentToHtml is the Phase 1 target: identical output for preview
 *                     and PDF by construction (G-DRY-RENDER, G-COMPAT).
 */

import type { DocumentLayout, Band, PageSetup, RenderCondition } from './documentLayout';
import type { DocumentConfig, DocumentRenderData } from './documentConfig';
import { THEME_PRESETS } from './documentConfig';
import { renderAllSections, renderLayoutToHtml } from './renderSections';
import { bandStyleCss, elementStyleCss, borderCss } from './bandStyle';

const PAGE_DIMENSIONS_PT: Record<string, { w: number; h: number }> = {
  letter: { w: 612, h: 792 },
  legal: { w: 612, h: 1008 },
  a4: { w: 595, h: 842 },
};

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Strip characters that could break out of a CSS declaration or the surrounding
 * `<style>` block (G-SECURITY). Custom theme values (Phase E2) are user-authored, so we
 * remove `< > { } ;` and backslashes while preserving commas/quotes/parens needed for
 * color functions and font-family stacks. Empty/blank input returns ''.
 */
function sanitizeCssValue(value: string | undefined): string {
  if (!value) return '';
  return value.replace(/[<>{};\\]/g, '').trim();
}



/**
 * Resolve the effective page watermark for a DocumentConfig: a custom `page.watermark`
 * (text/opacity/angle) takes precedence over the legacy `draftWatermark` boolean (→ "DRAFT").
 * Returns null when no watermark applies. (G-LAYOUT)
 */
function resolveConfigWatermark(config: DocumentConfig): { text: string; opacity: number; angle: number } | null {
  const w = config.page?.watermark;
  if (w && w.text) return { text: w.text, opacity: w.opacity ?? 0.08, angle: w.angle ?? -45 };
  if (config.draftWatermark) return { text: 'DRAFT', opacity: 0.1, angle: -45 };
  return null;
}

// ---------------------------------------------------------------------------
// Band / Element Styling (A2)
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Page CSS
// ---------------------------------------------------------------------------

/**
 * @process renderPageCss
 * Steps: Build the @page + .doc-page CSS for size/orientation/margins/border/watermark.
 * Idempotency: REQUIRED (pure).
 */
export function renderPageCss(page: PageSetup): string {
  const dims = PAGE_DIMENSIONS_PT[page.size] ?? PAGE_DIMENSIONS_PT.letter;
  const isLandscape = page.orientation === 'landscape';
  const w = isLandscape ? dims.h : dims.w;
  const m = page.margins;
  const parts: string[] = [];
  parts.push(`@page { size: ${page.size} ${page.orientation}; margin: ${m.top ?? 72}pt ${m.right ?? 72}pt ${m.bottom ?? 72}pt ${m.left ?? 72}pt; }`);
  parts.push(`.doc-page { position: relative; width: ${w}pt; margin: 0 auto; box-sizing: border-box; padding: ${m.top ?? 72}pt ${m.right ?? 72}pt ${m.bottom ?? 72}pt ${m.left ?? 72}pt; }`);
  if (page.border) {
    parts.push(`.doc-page { border: ${borderCss(page.border)} }`);
  }
  if (page.watermark && page.watermark.text) {
    const wm = page.watermark;
    parts.push(
      `.doc-watermark { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }`,
    );
    parts.push(
      `.doc-watermark span { transform: rotate(${wm.angle ?? -45}deg); font-size: 96pt; font-weight: 700; color: #000; opacity: ${wm.opacity ?? 0.08}; white-space: nowrap; }`,
    );
  }
  parts.push(`.doc-band { position: relative; z-index: 1; }`);
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Layout-Only Band Rendering (pre-A2 — binding markers for dynamic bands)
// ---------------------------------------------------------------------------

function renderBand(band: Band, context?: { hasViolations?: boolean }): string {
  if (band.enabled === false) return '';

  // Conditional rendering (A2)
  if (band.condition && band.condition !== 'always' && context) {
    if (band.condition === 'hasViolations' && !context.hasViolations) return '';
    if (band.condition === 'noViolations' && context.hasViolations) return '';
  }

  // Page break band
  if (band.kind === 'pageBreak') {
    return '<div style="page-break-before:always;break-before:page;"></div>';
  }

  // Text elements carry sanitized HTML; concatenate them.
  const inner = band.elements
    .map((el) => {
      const styleAttr = el.style ? ` style="${escapeAttr(elementStyleCss(el.style))}"` : '';
      if (el.type === 'text' && el.html) {
        return styleAttr ? `<div${styleAttr}>${el.html}</div>` : el.html;
      }
      if (el.type === 'spacer') {
        return `<div class="doc-spacer"${styleAttr}></div>`;
      }
      if (el.type === 'image' && el.html) {
        return `<img src="${escapeAttr(el.html)}" class="doc-image"${styleAttr}>`;
      }
      if (el.binding) {
        // Dynamic band: emit a binding marker the data layer expands later.
        return `<!-- band:${band.kind} bind:${el.binding.source}${el.binding.displayMode ? ':' + el.binding.displayMode : ''} -->`;
      }
      return '';
    })
    .join('\n');

const bandStyle = band.style ? ` style="${escapeAttr(bandStyleCss(band.style))}"` : '';
  // Emit both the legacy alias (page-break-inside) and the W3C standard (break-inside) so this
  // layout-only path stays in lockstep with the data-driven renderSections path (G-DRY-RENDER).
  const keepTogether = band.keepTogether ? ' data-keep-together="true" style="page-break-inside:avoid;break-inside:avoid;"' : '';
  // If both bandStyle and keepTogether have styles, merge them. Strip any trailing ';' from
  // bandStyleCss (it ends with one when a border is present) so the junction never yields ';;',
  // matching the data-driven renderSections path (G-DRY-RENDER).
  let attrs = '';
  if (band.style && band.keepTogether) {
    attrs = ` style="${escapeAttr(bandStyleCss(band.style).replace(/;+$/, '') + ';page-break-inside:avoid;break-inside:avoid;')}" data-keep-together="true"`;
  } else {
    attrs = bandStyle + keepTogether;
  }

  return `<section class="doc-band doc-band-${band.kind}" data-band="${escapeAttr(band.kind)}"${attrs}>${inner}</section>`;
}

// ---------------------------------------------------------------------------
// Layout-Only Entry Point (existing — renders binding markers, no data)
// ---------------------------------------------------------------------------

/**
 * @process renderDocumentLayoutToHtml
 * Steps: 1. Build page CSS. 2. Render header (if any), body bands (in order, skipping disabled),
 *        footer (if any), inside a .doc-page wrapper (with watermark overlay if set).
 * Inputs: DocumentLayout.
 * Outputs: An HTML fragment string (CSS + .doc-page). Deterministic.
 * Idempotency: REQUIRED (pure).
 */
export function renderDocumentLayoutToHtml(layout: DocumentLayout): string {
  const css = renderPageCss(layout.page);
  const watermark = layout.page.watermark?.text
    ? `<div class="doc-watermark"><span>${escapeAttr(layout.page.watermark.text)}</span></div>`
    : '';
  const header = layout.header && layout.header.enabled !== false ? renderBand(layout.header) : '';
  const footer = layout.footer && layout.footer.enabled !== false ? renderBand(layout.footer) : '';
  const body = layout.body.map((b) => renderBand(b)).join('\n');
  return [
    `<style>${css}</style>`,
    `<div class="doc-page" data-doc-type="${escapeAttr(layout.documentType)}">`,
    watermark,
    header,
    body,
    footer,
    `</div>`,
  ].filter(Boolean).join('\n');
}

// ---------------------------------------------------------------------------
// Document Default CSS (A2 — shared base stylesheet for section renderers)
// ---------------------------------------------------------------------------

/**
 * @process renderDocumentCss
 * Steps: Build the base stylesheet for the document sections (matches Handlebars CSS).
 *        When `branding` is provided, injects --brand-primary / --brand-secondary CSS
 *        custom properties and uses them for section titles, violation accents, and the
 *        header border -- ensuring brand-colored PDFs via the shared renderer (A4).
 * Inputs: DocumentConfig (for page size, orientation, layout mode, draft watermark),
 *         optional branding colors from DocumentRenderData.branding.
 * Outputs: CSS string. Deterministic.
 * Idempotency: REQUIRED (pure).
 */
export function renderDocumentCss(
  config: DocumentConfig,
  branding?: { primary: string; secondary?: string },
): string {
  const size = config.pageSize || 'letter';
  const orientation = config.orientation || 'portrait';
  const page = config.page;
  // G-LAYOUT: page margins are configured in INCHES (PageSettings.marginsInches), default 0.5in.
  const m = page?.marginsInches ?? {};

  const parts: string[] = [];

  // Determine effective branding colors. An explicitly chosen themePreset is the author's
  // deliberate decision for THIS template, so it outranks the department's runtime branding; the
  // 'none' preset ("Department Default") is the explicit opt-in to runtime branding. Without this,
  // the worker (which always passes department branding) silently overrode every preset in the PDF
  // while the preview showed the preset — the Finding-2 divergence.
  // Priority 1: template themePreset (!= 'none') — author's explicit per-document choice
  // Priority 2: runtime branding (department tenant colors at render time)
  // Priority 3: no branding (CSS falls back to built-in neutral colors via var() fallbacks)
  const presetColors = config.themePreset && config.themePreset !== 'none'
    ? THEME_PRESETS.find((p) => p.id === config.themePreset)
    : undefined;
  const effectiveBranding = (presetColors?.primary ? presetColors : undefined) ?? branding;

  // Phase E2: custom theme overrides win over the preset/runtime branding for THIS document,
  // because the author explicitly chose these values on the template. Sanitized to prevent
  // CSS/style-block breakout. Empty fields fall back to the preset, then the renderer default.
  const tc = config.themeCustom;
  const customPrimary = sanitizeCssValue(tc?.primary);
  const customAccent = sanitizeCssValue(tc?.accent);
  const customHeading = sanitizeCssValue(tc?.heading);
  const customBaseFont = sanitizeCssValue(tc?.baseFont);

  const primary = customPrimary || effectiveBranding?.primary || '';
  const secondary = customAccent
    || (effectiveBranding as { secondary?: string } | undefined)?.secondary
    || primary;
  // Heading color defaults to primary so titles stay on-brand unless explicitly themed.
  const heading = customHeading || primary;

  // Inject brand CSS custom properties. All brand-colored rules use var(--brand-*, <fallback>)
  // so documents render correctly both with and without branding. --brand-primary stays first
  // so existing prefix assertions (`:root { --brand-primary`) hold.
  const rootVars: string[] = [];
  if (primary) rootVars.push(`--brand-primary: ${primary}`);
  if (secondary) rootVars.push(`--brand-secondary: ${secondary}`);
  if (heading) rootVars.push(`--brand-heading: ${heading}`);
  if (rootVars.length) {
    parts.push(`:root { ${rootVars.join('; ')}; }`);
  }

  const bodyFont = customBaseFont || `'Arial', 'Helvetica', sans-serif`;

  parts.push(`@page { size: ${size} ${orientation}; margin: ${m.top ?? 0.5}in ${m.right ?? 0.5}in ${m.bottom ?? 0.5}in ${m.left ?? 0.5}in; }`);
  parts.push(`body { margin: 0; padding: 0; font-family: ${bodyFont}; font-size: 10pt; line-height: 1.4; color: #333; orphans: 2; widows: 2; }`);
  // Graceful pagination (G-PAGINATION-01): keep small logical blocks intact across page
  // breaks and keep a heading with the content that follows it, so a block never strands a
  // heading or a couple of orphan lines onto the next page. Large groups may still break.
  parts.push(`.checklist-item, .violation-item, .photo-item, .signature-entry, .comments-box { page-break-inside: avoid; break-inside: avoid; }`);
  parts.push(`.zone-title, .section-title, .form-response-form-name, .form-response-section-title, .report-title, .report-subtitle, h1, h2, h3, h4 { page-break-after: avoid; break-after: avoid; }`);
  // G-LAYOUT: optional page border (PageSettings.border).
  if (page?.border) {
    parts.push(`body { border: ${page.border.width}pt ${page.border.style} ${page.border.color}; }`);
  }

  // Section styles (matching Handlebars CSS; brand-color rules use CSS custom properties)
  parts.push(`.report-section { margin-bottom: 12pt; }`);
  parts.push(`.report-header { text-align: center; margin-bottom: 16pt; border-bottom: 3px solid var(--brand-primary, #ccc); padding-bottom: 12pt; }`);
  parts.push(`.header-row { display: flex; align-items: center; justify-content: center; gap: 16pt; }`);
  parts.push(`.header-center { flex: 1; text-align: center; }`);
  parts.push(`.header-logo { flex-shrink: 0; }`);
  parts.push(`.header-logo-left { order: -1; }`);
  parts.push(`.header-logo-right { order: 1; }`);
  parts.push(`.logo-img { max-height: 72pt; max-width: 120pt; }`);
  parts.push(`.header-dept-name { font-size: 14pt; font-weight: 700; }`);
  parts.push(`.header-dept-detail { font-size: 9pt; color: #555; }`);
  parts.push(`.report-title { font-size: 16pt; font-weight: 700; margin: 8pt 0 4pt; text-align: center; color: var(--brand-heading, var(--brand-primary, #333)); }`);
  parts.push(`.report-subtitle { font-size: 11pt; color: #555; text-align: center; }`);

  // Recipient
  parts.push(`.info-table { width: 100%; border-collapse: collapse; }`);
  parts.push(`.info-table th { text-align: left; font-weight: 600; padding: 4pt 8pt; width: 30%; color: #555; }`);
  parts.push(`.info-table td { padding: 4pt 8pt; }`);
  parts.push(`.letter-address-block { margin-bottom: 12pt; }`);
  parts.push(`.letter-address-line { line-height: 1.6; }`);

  // Subject + Salutation
  parts.push(`.subject-line { font-size: 11pt; font-weight: 700; margin-bottom: 8pt; }`);
  parts.push(`.salutation { margin-bottom: 8pt; }`);

  // Body text
  parts.push(`.body-paragraph { margin-bottom: 8pt; }`);

  // Violations
  parts.push(`.zone-group { margin-bottom: 12pt; }`);
  parts.push(`.zone-title { font-size: 11pt; font-weight: 600; margin-bottom: 4pt; border-bottom: 1px solid #ddd; padding-bottom: 2pt; color: var(--brand-heading, var(--brand-primary, #333)); }`);
  parts.push(`.checklist-item { display: flex; align-items: flex-start; gap: 6pt; margin-bottom: 4pt; word-break: break-all; }`);
  parts.push(`.checklist-code { font-weight: 700; min-width: 24pt; word-break: break-all; }`);
  parts.push(`.checklist-ref { color: #777; font-size: 9pt; word-break: break-all; }`);
  parts.push(`.violation-item { margin-bottom: 8pt; border-left: 3px solid var(--brand-primary, #ccc); padding-left: 8pt; background: #fafafa; }`);
  parts.push(`.violation-code { font-weight: 700; color: var(--brand-primary, #333); }`);
  parts.push(`.violation-ref { color: #777; font-size: 9pt; }`);
  parts.push(`.violation-location { color: #555; font-size: 9pt; margin-top: 2pt; }`);
  parts.push(`.violation-table th { background: var(--brand-primary, #f5f5f5); color: #fff; font-weight: 600; border-bottom: 2px solid var(--brand-primary, #ccc); }`);
  parts.push(`.violation-table td { border-bottom: 1px solid #eee; }`);
  parts.push(`.no-violations { color: #28a745; font-style: italic; }`);
  // Question-grouped violations (G-VIOL-QGROUP): question heading + nested occurrences.
  parts.push(`.question-group { margin-bottom: 12pt; page-break-inside: avoid; break-inside: avoid; }`);
  parts.push(`.question-title { font-size: 11pt; font-weight: 600; margin-bottom: 4pt; border-bottom: 1px solid #ddd; padding-bottom: 2pt; color: var(--brand-heading, var(--brand-primary, #333)); }`);
  parts.push(`.additional-violations .question-title { color: #555; font-style: italic; }`);
  parts.push(`.violation-occurrences { list-style: none; margin: 2pt 0 4pt 28pt; padding: 0; }`);
  parts.push(`.violation-occurrence { font-size: 9pt; color: #555; margin-bottom: 6pt; }`);
  parts.push(`.occ-spatial { font-weight: 600; color: #333; margin-bottom: 2pt; }`);
  parts.push(`.occ-notes { white-space: pre-wrap; margin: 2pt 0; }`);
  parts.push(`.occ-label { font-weight: 600; }`);
  parts.push(`.occ-date-row { display: flex; gap: 16pt; margin: 4pt 0; padding: 4pt 8pt; background: #f8f8f8; border: 1px solid #eee; border-radius: 2pt; }`);
  parts.push(`.occ-date-cell { display: flex; flex-direction: column; min-width: 80pt; }`);
  parts.push(`.occ-date-label { font-size: 8pt; color: #888; font-weight: 600; text-transform: uppercase; }`);
  parts.push(`.occ-date-value { font-size: 9pt; color: #333; }`);
  parts.push(`.violation-separator { border-bottom: 1px dashed #ccc; margin: 8pt 0; }`);
  parts.push(`.narrative-occurrence { font-size: 9pt; color: #555; margin: 4pt 0 4pt 12pt; padding-left: 8pt; border-left: 2px solid #ddd; }`);

  // Photos
  parts.push(`.photo-gallery { display: flex; flex-wrap: wrap; gap: 8pt; }`);
  parts.push(`.photo-item { flex: 0 0 auto; }`);
  parts.push(`.photo-item img { max-width: 180pt; max-height: 140pt; display: block; }`);
  parts.push(`.photo-caption { font-size: 8pt; color: #555; text-align: center; margin-top: 2pt; }`);
  parts.push(`.photo-date { font-size: 7pt; color: #888; text-align: center; }`);
  parts.push(`.photo-violation-tag { font-size: 8pt; color: #c00; margin-top: 2pt; word-break: break-all; overflow-wrap: break-word; }`);
  // Photo layout variants (G-506): the gallery/appendix layouts + thumbnail-size control now have
  // real CSS instead of emitting inert classes. `inline` placement is handled structurally by the
  // assembler (photos render beneath their violation), so it needs no gallery override here.
  parts.push(`.photo-gallery.photo-layout-appendix { flex-direction: column; }`);
  parts.push(`.photo-gallery.photo-layout-appendix .photo-item img { max-width: 100%; max-height: none; }`);
  parts.push(`.photo-item.photo-size-small img { max-width: 110pt; max-height: 90pt; }`);
  parts.push(`.photo-item.photo-size-medium img { max-width: 180pt; max-height: 140pt; }`);
  parts.push(`.photo-item.photo-size-large img { max-width: 260pt; max-height: 200pt; }`);
  // Inline photos rendered beneath a violation (DFE-005).
  parts.push(`.violation-inline-photos { display: flex; flex-wrap: wrap; gap: 8pt; margin: 4pt 0 6pt 28pt; }`);
  parts.push(`.violation-inline-photos .photo-item img { max-width: 150pt; max-height: 120pt; display: block; }`);

  // Comments
  parts.push(`.comments-box { border: 1px solid #ccc; padding: 8pt; margin-bottom: 8pt; }`);
  parts.push(`.comments-label { font-weight: 600; margin-bottom: 4pt; }`);
  parts.push(`.comments-empty { border-style: dashed; }`);
  parts.push(`.additional-field { margin-bottom: 4pt; }`);
  parts.push(`.additional-field-label { font-weight: 600; margin-right: 6pt; }`);

  // Contact
  parts.push(`.contact-intro { margin-bottom: 6pt; }`);
  parts.push(`.contact-line { margin-bottom: 2pt; }`);

  // Signatures
  // Long printed names / role labels must wrap within the fixed-width signature entry
  // rather than clip or overflow the block (DFE-008).
  parts.push(`.signature-block { display: flex; flex-wrap: wrap; gap: 24pt; margin-top: 16pt; }`);
  parts.push(`.signature-entry { flex: 1 1 160pt; min-width: 160pt; max-width: 100%; overflow-wrap: anywhere; word-break: break-word; }`);
  parts.push(`.signature-label { font-weight: 600; margin-bottom: 4pt; overflow-wrap: anywhere; word-break: break-word; }`);
  parts.push(`.signature-image img { max-height: 48pt; max-width: 100%; }`);
  parts.push(`.signature-line { border-bottom: 1px solid #333; min-width: 160pt; height: 24pt; }`);
  parts.push(`.signature-initials-line { min-width: 48pt; }`);
  parts.push(`.signature-name { font-size: 9pt; margin-top: 2pt; overflow-wrap: anywhere; word-break: break-word; }`);
  parts.push(`.signature-title { font-size: 9pt; color: #555; overflow-wrap: anywhere; word-break: break-word; }`);
  parts.push(`.signature-date { font-size: 9pt; color: #555; margin-top: 2pt; }`);

  // Legal
  parts.push(`.legal-section { font-size: 8pt; color: #555; border-top: 1px solid #ccc; padding-top: 8pt; margin-top: 12pt; }`);
  parts.push(`.legal-text { border-left: 3px solid var(--brand-secondary, var(--brand-primary, #ccc)); padding-left: 6pt; background: #f9f9f9; }`);

  // Footer
  parts.push(`.report-footer { text-align: center; font-size: 8pt; color: #777; border-top: 1px solid #ccc; padding-top: 6pt; margin-top: 16pt; }`);
  parts.push(`.footer-custom { margin-top: 4pt; }`);

  // Section titles (brand-colored, matching Handlebars .section-title)
  parts.push(`.section-title { font-size: 12pt; font-weight: 700; margin-bottom: 8pt; color: var(--brand-primary, #333); border-bottom: 1px solid var(--brand-primary, #ccc); padding-bottom: 3pt; }`);

  // Form responses
  parts.push(`.form-response-group { margin-bottom: 12pt; }`);
  parts.push(`.form-response-form-name { font-size: 12pt; font-weight: 700; margin-bottom: 6pt; }`);
  parts.push(`.form-response-section-title { font-size: 11pt; font-weight: 600; margin-bottom: 4pt; color: #555; }`);
  parts.push(`.form-response-table { width: 100%; border-collapse: collapse; margin-bottom: 8pt; }`);
  parts.push(`.form-response-label { text-align: left; font-weight: 600; padding: 4pt 8pt; width: 30%; color: #555; }`);
  parts.push(`.form-response-value { padding: 4pt 8pt; }`);
  parts.push(`.form-response-image { max-width: 200pt; max-height: 150pt; }`);
  parts.push(`.form-response-no-image { color: #999; font-style: italic; }`);

  // Static content
  parts.push(`.static-attachment-note { font-style: italic; color: #555; }`);

  // Watermark — custom page.watermark (text/opacity/angle) overrides the legacy draftWatermark flag.
  const wm = resolveConfigWatermark(config);
  if (wm) {
    parts.push(`.doc-watermark { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }`);
    parts.push(`.doc-watermark span { transform: rotate(${wm.angle}deg); font-size: 96pt; font-weight: 700; color: #000; opacity: ${wm.opacity}; white-space: nowrap; }`);
  }

  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Data-Driven Entry Point (A2 — fully expanded sections from DocumentRenderData)
// ---------------------------------------------------------------------------

/**
 * @process renderDocumentToHtml
 * Steps: 1. Build document CSS. 2. Build watermark (if draft). 3. Render all sections via
 *        renderAllSections (from renderSections.ts) using the resolved DocumentRenderData.
 *        4. Wrap in HTML document structure.
 * Inputs: DocumentRenderData (resolved data + config).
 * Outputs: Complete HTML document string. Deterministic — identical in preview and PDF.
 * Idempotency: REQUIRED (pure).
 */
export function renderDocumentToHtml(data: DocumentRenderData): string {
  const config = data.documentConfig;
  const css = renderDocumentCss(config, data.branding);
  const wm = resolveConfigWatermark(config);
  const watermark = wm
    ? `<div class="doc-watermark"><span>${escapeAttr(wm.text)}</span></div>`
    : '';
  const body = renderAllSections(data);

  return [
    `<!DOCTYPE html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="UTF-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1.0">`,
    `<title>${escapeAttr(config.header?.customTitle || 'Document')}</title>`,
    `<style>${css}</style>`,
    `</head>`,
    `<body class="layout-${escapeAttr(config.layoutMode)}">`,
    watermark,
    body,
    `</body>`,
    `</html>`,
  ].filter(Boolean).join('\n');
}

/**
 * @process renderLayoutDocumentToHtml
 * Steps:   Same page chrome (CSS, margins/border/watermark) as renderDocumentToHtml, but the
 *          body is rendered from the DocumentLayout's band order (the canvas builder's model)
 *          via renderLayoutToHtml — so a canvas-authored document prints in its band order.
 * Inputs:  layout (band order) + resolved DocumentRenderData (page settings, branding, data).
 * Outputs: complete HTML document string. Deterministic — identical in preview and PDF.
 * Idempotency: REQUIRED (pure).
 */
export function renderLayoutDocumentToHtml(layout: DocumentLayout, data: DocumentRenderData): string {
  const config = data.documentConfig;
  const css = renderDocumentCss(config, data.branding);
  const wm = resolveConfigWatermark(config);
  const watermark = wm
    ? `<div class="doc-watermark"><span>${escapeAttr(wm.text)}</span></div>`
    : '';
  const body = renderLayoutToHtml(layout, data);

  return [
    `<!DOCTYPE html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="UTF-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1.0">`,
    `<title>${escapeAttr(config.header?.customTitle || 'Document')}</title>`,
    `<style>${css}</style>`,
    `</head>`,
    `<body class="layout-${escapeAttr(config.layoutMode)}">`,
    watermark,
    body,
    `</body>`,
    `</html>`,
  ].filter(Boolean).join('\n');
}
